import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "../pages/Login";
import MainLayout from "../components/layout/MainLayout";
import PageLayout from "../components/layout/PageLayout";
import ProtectedRoute from "../components/auth/ProtectedRoute";
import AdminRoute from "../components/auth/AdminRoute";

const Dashboard = lazy(() => import("../pages/Dashboard"));
const FarmAgeStatus = lazy(() => import("../pages/accounting/FarmAgeStatus"));
const Inbound = lazy(() => import("../pages/Inbound"));
const EggInbound = lazy(() => import("../pages/inbound/EggInbound"));
const EggInboundExcel = lazy(() => import("../pages/inbound/EggInboundExcel"));
const EggInboundHistory = lazy(() => import("../pages/inbound/EggInboundHistory"));
const MaterialsInbound = lazy(() => import("../pages/inbound/MaterialsInbound"));
const SubMaterialsInboundExcel = lazy(() => import("../pages/inbound/SubMaterialsInboundExcel"));
const SubMaterialsInboundHistory = lazy(() => import("../pages/inbound/SubMaterialsInboundHistory"));
const Inventory = lazy(() => import("../pages/Inventory"));
const OrdersModule = lazy(() => import("../pages/OrdersModule"));
const CSManagement = lazy(() => import("../pages/cs/CSManagement"));
const CSHistory = lazy(() => import("../pages/cs/CSHistory"));
const Sales = lazy(() => import("../pages/Sales"));
const Stocktaking = lazy(() => import("../pages/Stocktaking"));
const StocktakingDashboard = lazy(() => import("../pages/StocktakingDashboard"));
const TraceabilityInbound = lazy(() => import("../pages/TraceabilityInbound"));
const TraceabilityInboundHistory = lazy(() => import("../pages/TraceabilityInboundHistory"));
const TraceabilityManage = lazy(() => import("../pages/TraceabilityManage"));
const TraceabilityOutbound = lazy(() => import("../pages/TraceabilityOutbound"));
const TraceabilityOutboundHistory = lazy(() => import("../pages/TraceabilityOutboundHistory"));
const Orders = lazy(() => import("../pages/orders/Orders"));
const ProductPrices = lazy(() => import("../pages/orders/ProductPrices"));
const OrderStatus = lazy(() => import("../pages/orders/OrderStatus"));
const OrdersHistory = lazy(() => import("../pages/orders/OrdersHistory"));
const OrdersExcel = lazy(() => import("../pages/orders/OrdersExcel"));
const OrderEggMatching = lazy(() => import("../pages/orders/OrderEggMatching"));
const QuantityConfirmation = lazy(() => import("../pages/orders/QuantityConfirmation"));
const Instructions = lazy(() => import("../pages/orders/Instructions"));
const InstructionHistory = lazy(() => import("../pages/orders/InstructionHistory"));
const ProductionIssueHistory = lazy(() => import("../pages/orders/ProductionIssueHistory"));
const DispatchManagement = lazy(() => import("../pages/orders/DispatchManagement"));
const DispatchHistory = lazy(() => import("../pages/orders/DispatchHistory"));
const EggUsageMobile = lazy(() => import("../pages/orders/EggUsageMobile"));
const InstructionCompletionMobile = lazy(() => import("../pages/orders/InstructionCompletionMobile"));
const Employees = lazy(() => import("../pages/hr/Employees"));
const AddEmployee = lazy(() => import("../pages/hr/AddEmployee"));
const CustomerInfo = lazy(() => import("../pages/accounting/CustomerInfo"));
const FarmInfo = lazy(() => import("../pages/accounting/FarmInfo"));
const FactoryInfo = lazy(() => import("../pages/accounting/FactoryInfo"));
const PalletLocationInfo = lazy(() => import("../pages/accounting/PalletLocationInfo"));
const ProductInfo = lazy(() => import("../pages/accounting/ProductInfo"));
const ProductSubsidiaryUsage = lazy(() => import("../pages/accounting/ProductSubsidiaryUsage"));
const ProductSubsidiaryUsageExcel = lazy(() => import("../pages/accounting/ProductSubsidiaryUsageExcel"));
const CustomerExcel = lazy(() => import("../pages/accounting/CustomerExcel"));
const FarmExcel = lazy(() => import("../pages/accounting/FarmExcel"));
const ProductExcel = lazy(() => import("../pages/accounting/ProductExcel"));
const SubsidiaryMaterialsInfo = lazy(() => import("../pages/accounting/SubsidiaryMaterialsInfo"));
const SubsidiaryMaterialsExcel = lazy(() => import("../pages/accounting/SubsidiaryMaterialsExcel"));
const SubsidiaryMaterialsCompanyInfo = lazy(() => import("../pages/accounting/SubsidiaryMaterialsCompanyInfo"));
const SubsidiaryMaterialsCompanyExcel = lazy(() => import("../pages/accounting/SubsidiaryMaterialsCompanyExcel"));
const EggInventory = lazy(() => import("../pages/inventory/EggInventory"));
const EggInventoryExcel = lazy(() => import("../pages/inventory/EggInventoryExcel"));
const EggInventoryHistory = lazy(() => import("../pages/inventory/EggInventoryHistory"));
const EggInventoryLocations = lazy(() => import("../pages/inventory/EggInventoryLocations"));
const EggDailyStock = lazy(() => import("../pages/inventory/EggDailyStock"));
const GoodsInventory = lazy(() => import("../pages/inventory/GoodsInventory"));
const GoodsInventoryExcel = lazy(() => import("../pages/inventory/GoodsInventoryExcel"));
const GoodsInventoryHistory = lazy(() => import("../pages/inventory/GoodsInventoryHistory"));
const ProductDailyStock = lazy(() => import("../pages/inventory/ProductDailyStock"));
const MaterialsInventory = lazy(() => import("../pages/inventory/MaterialsInventory"));
const MaterialsInventoryHistory = lazy(() => import("../pages/inventory/MaterialsInventoryHistory"));
const MaterialsInventoryExcel = lazy(() => import("../pages/inventory/MaterialsInventoryExcel"));
const InboundOutboundStatus = lazy(() => import("../pages/sales/InboundOutboundStatus"));
const TargetPerformance = lazy(() => import("../pages/sales/TargetPerformance"));
const EggPalletLabelsPrint = lazy(() => import("../pages/print/EggPalletLabelsPrint"));
const SubMaterialsPurchaseOrderPrint = lazy(() => import("../pages/print/SubMaterialsPurchaseOrderPrint"));
const StocktakingSessionDetail = lazy(() => import("../pages/stocktaking/StocktakingSessionDetail"));
const StocktakingRecordHistory = lazy(() => import("../pages/stocktaking/StocktakingRecordHistory"));
const StocktakingMobile = lazy(() => import("../pages/stocktaking/StocktakingMobile"));

export default function Router() {
  return (
    <BrowserRouter>
      <Suspense fallback={<div className="route-loading" role="status">화면을 불러오는 중...</div>}>
        <Routes>
          <Route element={<MainLayout />}>
          <Route path="/" element={<Login />} />
          <Route
            path="/print/egg-pallet-labels"
            element={
              <ProtectedRoute>
                <EggPalletLabelsPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/print/submaterial-purchase-orders"
            element={
              <ProtectedRoute>
                <SubMaterialsPurchaseOrderPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/egg-usage-mobile"
            element={
              <ProtectedRoute>
                <EggUsageMobile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/fresh/instruction-completion/mobile"
            element={
              <ProtectedRoute>
                <InstructionCompletionMobile mode="fresh" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/baked/instruction-completion/mobile"
            element={
              <ProtectedRoute>
                <InstructionCompletionMobile mode="baked" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/liquid/instruction-completion/mobile"
            element={
              <ProtectedRoute>
                <InstructionCompletionMobile mode="liquid" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stocktaking/mobile"
            element={
              <ProtectedRoute>
                <StocktakingMobile />
              </ProtectedRoute>
            }
          />
          <Route element={<ProtectedRoute><PageLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />

            <Route path="/hr" element={<Employees />} />
            <Route path="/hr/employees" element={<Navigate to="/hr" replace />} />
            <Route path="/hr/add" element={<AdminRoute message="직원 등록은 관리자만 가능합니다."><AddEmployee /></AdminRoute>} />

            <Route path="/accounting" element={<Navigate to="/accounting/customer-info" replace />} />
            <Route path="/accounting/customer-info" element={<CustomerInfo />} />
            <Route path="/accounting/customer-info/excel" element={<CustomerExcel />} />
            <Route path="/accounting/farm-info" element={<FarmInfo />} />
            <Route path="/accounting/farm-info/age-status" element={<FarmAgeStatus />} />
            <Route path="/accounting/farm-info/excel" element={<FarmExcel />} />
            <Route path="/accounting/factory-info" element={<FactoryInfo />} />
            <Route path="/accounting/factory-info/pallet-locations" element={<PalletLocationInfo />} />
            <Route path="/accounting/product-info" element={<ProductInfo />} />
            <Route path="/accounting/product-subsidiary-usage" element={<ProductSubsidiaryUsage />} />
            <Route path="/accounting/product-info/excel" element={<ProductExcel />} />
            <Route path="/accounting/product-subsidiary-usage/excel" element={<ProductSubsidiaryUsageExcel />} />
            <Route path="/accounting/subsidiary-materials" element={<SubsidiaryMaterialsInfo />} />
            <Route path="/accounting/subsidiary-materials/excel" element={<SubsidiaryMaterialsExcel />} />
            <Route path="/accounting/subsidiary-materials-companies" element={<SubsidiaryMaterialsCompanyInfo />} />
            <Route path="/accounting/subsidiary-materials-companies/excel" element={<SubsidiaryMaterialsCompanyExcel />} />

            <Route path="/inbound" element={<Inbound />} />
            <Route path="/inbound/egg" element={<EggInbound />} />
            <Route path="/inbound/egg/excel" element={<EggInboundExcel />} />
            <Route path="/inbound/egg/history" element={<EggInboundHistory />} />
            <Route path="/inbound/submaterials" element={<MaterialsInbound />} />
            <Route path="/inbound/submaterials/excel" element={<SubMaterialsInboundExcel />} />
            <Route path="/inbound/submaterials/history" element={<SubMaterialsInboundHistory />} />

            <Route path="/inventory" element={<Inventory />} />
            <Route path="/inventory/egg-inventory" element={<EggInventory />} />
            <Route path="/inventory/egg-inventory/locations" element={<EggInventoryLocations />} />
            <Route path="/inventory/egg-inventory/daily-stock" element={<EggDailyStock />} />
            <Route path="/inventory/egg-inventory/excel" element={<EggInventoryExcel />} />
            <Route path="/inventory/egg-inventory/history" element={<EggInventoryHistory />} />
            <Route path="/inventory/goods-inventory" element={<GoodsInventory />} />
            <Route path="/inventory/goods-inventory/daily-stock" element={<ProductDailyStock />} />
            <Route path="/inventory/goods-inventory/excel" element={<GoodsInventoryExcel />} />
            <Route path="/inventory/goods-inventory/history" element={<GoodsInventoryHistory />} />
            <Route path="/inventory/materials-inventory" element={<MaterialsInventory />} />
            <Route path="/inventory/materials-inventory/history" element={<MaterialsInventoryHistory />} />
            <Route path="/inventory/materials-inventory/excel" element={<MaterialsInventoryExcel />} />

            <Route path="/orders" element={<OrdersModule />} />
            <Route path="/orders/order-management" element={<Navigate to="/dispatch/order-management" replace />} />
            <Route path="/orders/order-status" element={<Navigate to="/sales/sales-status" replace />} />
            <Route path="/orders/history" element={<Navigate to="/dispatch/order-history" replace />} />
            <Route path="/orders/excel" element={<Navigate to="/dispatch/order-excel" replace />} />

            <Route path="/sales" element={<Sales />} />
            <Route path="/sales/sales-status" element={<OrderStatus />} />
            <Route path="/sales/inbound-outbound-status" element={<InboundOutboundStatus />} />
            <Route path="/sales/target-performance" element={<TargetPerformance />} />
            <Route path="/sales/product-prices" element={<ProductPrices />} />
            <Route path="/stocktaking" element={<StocktakingDashboard />} />
            <Route path="/stocktaking/sessions" element={<Stocktaking />} />
            <Route path="/stocktaking/sessions/:sessionId" element={<StocktakingSessionDetail />} />
            <Route path="/stocktaking/losses" element={<StocktakingRecordHistory variant="loss" />} />
            <Route path="/stocktaking/founds" element={<StocktakingRecordHistory variant="found" />} />

            <Route path="/orders/fresh/quantity-confirmation" element={<QuantityConfirmation mode="normal" />} />
            <Route path="/orders/fresh/egg-matching" element={<OrderEggMatching mode="fresh" />} />
            <Route path="/orders/fresh/instructions" element={<Instructions mode="fresh" page="manage" />} />
            <Route path="/orders/fresh/instruction-completion" element={<Instructions mode="fresh" page="completion" />} />
            <Route path="/orders/fresh/instruction-history" element={<InstructionHistory mode="fresh" />} />

            <Route path="/orders/baked/quantity-confirmation" element={<QuantityConfirmation mode="baked" />} />
            <Route path="/orders/baked/egg-matching" element={<OrderEggMatching mode="baked" />} />
            <Route path="/orders/baked/instructions" element={<Instructions mode="baked" page="manage" />} />
            <Route path="/orders/baked/instruction-completion" element={<Instructions mode="baked" page="completion" />} />
            <Route path="/orders/baked/instruction-history" element={<InstructionHistory mode="baked" />} />

            <Route path="/orders/liquid/quantity-confirmation" element={<QuantityConfirmation mode="liquid" />} />
            <Route path="/orders/liquid/egg-matching" element={<OrderEggMatching mode="liquid" />} />
            <Route path="/orders/liquid/instructions" element={<Instructions mode="liquid" page="manage" />} />
            <Route path="/orders/liquid/instruction-completion" element={<Instructions mode="liquid" page="completion" />} />
            <Route path="/orders/liquid/instruction-history" element={<InstructionHistory mode="liquid" />} />
            <Route path="/orders/disposal-out-of-grade/production" element={<ProductionIssueHistory variant="production" />} />
            <Route path="/orders/disposal-out-of-grade/disposals" element={<ProductionIssueHistory variant="disposal" />} />
            <Route path="/orders/disposal-out-of-grade/out-of-grade" element={<ProductionIssueHistory variant="outOfGrade" />} />
            <Route path="/orders/disposal-out-of-grade/baked-washed" element={<ProductionIssueHistory variant="bakedWashed" />} />

            <Route path="/orders/quantity-confirmation" element={<Navigate to="/orders/fresh/quantity-confirmation" replace />} />
            <Route path="/orders/quantity-confirmation-baked" element={<Navigate to="/orders/baked/quantity-confirmation" replace />} />
            <Route path="/orders/quantity-confirmation-liquid" element={<Navigate to="/orders/liquid/quantity-confirmation" replace />} />
            <Route path="/orders/egg-matching" element={<Navigate to="/orders/fresh/egg-matching" replace />} />
            <Route path="/orders/instructions" element={<Navigate to="/orders/fresh/instructions" replace />} />
            <Route path="/orders/instruction-completion" element={<Navigate to="/orders/fresh/instruction-completion" replace />} />
            <Route path="/orders/work-orders" element={<Navigate to="/orders/fresh/instructions" replace />} />
            <Route path="/orders/instruction-history" element={<Navigate to="/orders/fresh/instruction-history" replace />} />
            <Route path="/dispatch" element={<Navigate to="/dispatch/order-management" replace />} />
            <Route path="/dispatch/order-management" element={<Orders />} />
            <Route path="/dispatch/product-prices" element={<Navigate to="/sales/product-prices" replace />} />
            <Route path="/dispatch/order-history" element={<OrdersHistory />} />
            <Route path="/dispatch/order-excel" element={<OrdersExcel />} />
            <Route path="/dispatch/management" element={<DispatchManagement />} />
            <Route path="/dispatch/history" element={<DispatchHistory />} />
            <Route path="/cs" element={<Navigate to="/cs/manage" replace />} />
            <Route path="/cs/manage" element={<CSManagement />} />
            <Route path="/cs/history" element={<CSHistory />} />
            <Route path="/traceability" element={<Navigate to="/traceability/inbound" replace />} />
            <Route path="/traceability/inbound" element={<TraceabilityInbound />} />
            <Route path="/traceability/inbound-history" element={<TraceabilityInboundHistory />} />
            <Route path="/traceability/outbound" element={<TraceabilityOutbound />} />
            <Route path="/traceability/outbound-history" element={<TraceabilityOutboundHistory />} />
            <Route path="/traceability/packing" element={<TraceabilityManage />} />
            <Route path="/traceability/trace" element={<Navigate to="/traceability/inbound" replace />} />
            <Route path="/traceability/history" element={<Navigate to="/traceability/inbound-history" replace />} />
            <Route path="/shipping" element={<Navigate to="/dispatch" replace />} />
            <Route path="/shipping/management" element={<Navigate to="/dispatch/management" replace />} />
            <Route path="/shipping/history" element={<Navigate to="/dispatch/history" replace />} />
            <Route path="/orders/shipping" element={<Navigate to="/dispatch/management" replace />} />
            <Route path="/orders/shipping-history" element={<Navigate to="/dispatch/history" replace />} />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
